import { TableOutlined } from '@ant-design/icons'
import { Button, Typography } from 'antd'
import { encodeFormFieldKey } from 'components/form/formHelpers'
import { SetFieldAccessoryValue } from 'components/form/formTypes'
import { resolvePressureCoefficient } from 'features/indicationTriage/cards/ffsCalc/mawpSection/pressureCoefficient/resolvePressureCoefficient'
import { Y304TableModal } from 'features/indicationTriage/cards/ffsCalc/mawpSection/pressureCoefficient/Y304TableModal'
import { useGeckoLocale } from 'features/locale'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useWatch } from 'react-hook-form'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

const { Text } = Typography

/** Matches the sibling material-stress accessory's indent under its field. */
const Accessory = styled.div`
  padding-left: 1.5rem;
`

/** Y is a ratio shown to ~2 dp; only flag an override beyond half a display unit. */
const Y_DIFF_TOLERANCE = 0.005

/**
 * Asset-model dataKeys the live geometry/Y are read from. NOTE: the accessory reconstructs the
 * form's RHF field name (`${dataKey}#${encodeFormFieldKey(componentId)}`) and knows length fields
 * are stored in metric-base METERS — both are `GeckoToggleForm` internals. The clean boundary
 * would be a live-value reader passed through `renderFieldAccessory`; that was scoped out to avoid
 * a shared-editor change. Do NOT copy this coupling into another accessory without that reader.
 */
const WALL_THICKNESS_DATAKEY = 'wall_thickness'
const RADIUS_DATAKEY = 'radius'
const PRESSURE_COEFFICIENT_DATAKEY = 'pressure_coefficient'

/** Parse a react-hook-form value to a finite number, or null (handles `''`, `undefined`, NaN). */
const parseFiniteNumber = (v: unknown): number | null => {
  const n =
    typeof v === 'string' ? parseFloat(v) : typeof v === 'number' ? v : NaN
  return Number.isFinite(n) ? n : null
}

/** Length fields store their metric base value in METERS (see `convertToMetricBaseUnit`). */
const metersToMm = (v: unknown): number | null => {
  const meters = parseFiniteNumber(v)
  return meters === null ? null : meters * 1000
}

/**
 * Inline accessory for the FFS `pressure_coefficient` (coefficient Y) field, sibling to the
 * allowable-stress (S) picker. Automates ASME B31.3-2022 §304.1.1:
 *
 *  - **t < D/6 (thin wall) or geometry not yet resolved** → opens the Table 304.1.1 material-
 *    family × metal-temperature picker; clicking a cell writes that Y via `setFieldValue`.
 *  - **t ≥ D/6 (thick wall)** → Y = (d + 2c)/(D + d + 2c). Prefills it into the field when empty,
 *    shows the spec + substituted calculation, and offers a "Use calculated" action; if the
 *    current value diverges it shows a `≠ calc` note.
 *
 * Geometry + Y are read LIVE from react-hook-form (the accessory renders inside `<FormProvider>`),
 * so the calculation and the divergence flag update as the user types — before saving.
 */
export const PressureCoefficientPickerAccessory: FC<{
  setFieldValue: SetFieldAccessoryValue
  /** The component id — equals the form group `uniqueID` used to build RHF field names. */
  componentId: string
}> = ({ setFieldValue, componentId }) => {
  const [open, setOpen] = useState(false)
  const { getPreferredMeasurementUnit } = useGeckoLocale()

  const enc = encodeFormFieldKey(componentId)
  const liveWall = useWatch({ name: `${WALL_THICKNESS_DATAKEY}#${enc}` })
  const liveRadius = useWatch({ name: `${RADIUS_DATAKEY}#${enc}` })
  const liveY = useWatch({ name: `${PRESSURE_COEFFICIENT_DATAKEY}#${enc}` })

  const tMm = metersToMm(liveWall)
  const radiusMm = metersToMm(liveRadius)
  const dMm = radiusMm === null ? null : radiusMm * 2
  const currentY = parseFiniteNumber(liveY)

  const resolution = useMemo(
    () => resolvePressureCoefficient({ tMm, dMm }),
    [tMm, dMm]
  )

  // Thick-wall only: prefill the computed Y once, and only when the field is empty. A one-shot
  // ref prevents a re-fire loop once the write lands back through the live value.
  const prefilledRef = useRef(false)
  useEffect(() => {
    if (
      resolution.state === 'formula' &&
      currentY === null &&
      !prefilledRef.current
    ) {
      prefilledRef.current = true
      setFieldValue(resolution.y)
    }
  }, [resolution, currentY, setFieldValue])

  if (resolution.state === 'formula') {
    const differs =
      currentY !== null && Math.abs(currentY - resolution.y) > Y_DIFF_TOLERANCE
    const lengthUnit = getPreferredMeasurementUnit(GeckoMeasurementUnit.Inches)
    const fmt = (mm: number): string =>
      GeckoMeasurement.CreateFromValue(mm, GeckoMeasurementUnit.Millimeters)
        .convertTo(lengthUnit)
        .num.toFixed(2)
    return (
      <Accessory>
        <div>
          <Text style={{ fontSize: '0.85rem' }}>
            Y = (d + 2c) / (D + d + 2c) ={' '}
            <strong>{resolution.y.toFixed(3)}</strong>
          </Text>
          <Button
            type='link'
            size='small'
            style={{ padding: '0 0 0 0.5rem' }}
            onClick={() => setFieldValue(resolution.y)}>
            Use calculated
          </Button>
          {differs && (
            <Text type='warning' style={{ fontSize: '0.85rem' }}>
              · current {currentY.toFixed(2)} ≠ calc
            </Text>
          )}
        </div>
        <div>
          <Text type='secondary' style={{ fontSize: '0.75rem' }}>
            ASME B31.3-2022 §304.1.1 (t ≥ D/6) · D = {fmt(resolution.D)}, t ={' '}
            {fmt(resolution.t)}, c = {fmt(resolution.c)} → d = D − 2t ={' '}
            {fmt(resolution.d)} {lengthUnit.name}
          </Text>
        </div>
        {resolution.c === 0 && (
          <div>
            <Text type='secondary' italic style={{ fontSize: '0.7rem' }}>
              c = 0: for straight pipe the mechanical allowance (thread/groove
              depth) is 0; corrosion/erosion is applied separately as future
              corrosion (FCA), not in the design thickness.
            </Text>
          </div>
        )}
      </Accessory>
    )
  }

  // Thin wall (t < D/6) or geometry incomplete → the Table 304.1.1 picker (needs no geometry).
  return (
    <Accessory>
      <Button
        type='link'
        size='small'
        icon={<TableOutlined />}
        onClick={() => setOpen(true)}
        style={{ padding: 0 }}>
        Pick coefficient Y (Table 304.1.1)
      </Button>
      <Y304TableModal
        open={open}
        onClose={() => setOpen(false)}
        onPick={(y) => setFieldValue(y)}
      />
    </Accessory>
  )
}
